import React, { useState, useEffect } from 'react';
import roleService from '../services/roleService';
import { FiPlus, FiEdit } from 'react-icons/fi';

const RoleForm = ({ role, permissions, onSave, onCancel }) => {
  const [formData, setFormData] = useState({ name: '', description: '', permissions: [] });
  const permissionValues = Object.values(permissions || {});

  useEffect(() => {
    if (role) setFormData({ name: role.name, description: role.description || '', permissions: role.permissions || [] });
  }, [role]);

  const handlePermissionChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      const newPermissions = checked ? [...prev.permissions, value] : prev.permissions.filter(p => p !== value);
      return { ...prev, permissions: newPermissions };
    });
  };

  const handleSelectAll = () => {
    setFormData((prev) => ({ ...prev, permissions: [...new Set(permissionValues)] }));
  };

  const handleClearAll = () => {
    setFormData((prev) => ({ ...prev, permissions: [] }));
  };

  const handleSubmit = (e) => { e.preventDefault(); onSave(formData); };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <input name="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Role Name" required className="p-2 border rounded-md w-full" />
      <textarea name="description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Description" className="p-2 border rounded-md w-full" rows="2"></textarea>
      <div className="border p-4 rounded-md">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold">Permissions</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-xs px-2 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={handleClearAll}
              className="text-xs px-2 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Clear all
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {Object.entries(permissions).map(([key, value]) => (
            <label key={key} className="flex items-center text-sm"><input type="checkbox" value={value} checked={formData.permissions.includes(value)} onChange={handlePermissionChange} className="mr-2" /> {value}</label>
          ))}
        </div>
      </div>
      <div className="flex justify-end space-x-3 pt-4"><button type="button" onClick={onCancel} className="bg-gray-200 px-4 py-2 rounded-md">Cancel</button><button type="submit" className="bg-primary text-white px-4 py-2 rounded-md">Save Role</button></div>
    </form>
  );
};

const RolesPage = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  const fetchData = async () => {
    const [rolesData, permsData] = await Promise.all([roleService.getRoles(), roleService.getPermissions()]);
    setRoles(rolesData);
    setPermissions(permsData);
  };

  useEffect(() => { fetchData(); }, []);

  const handleOpenModal = (role = null) => { setEditingRole(role); setIsModalOpen(true); };
  const handleCloseModal = () => { setEditingRole(null); setIsModalOpen(false); };

  const handleSave = async (formData) => {
    if (editingRole) { await roleService.updateRole(editingRole._id, formData); } 
    else { await roleService.createRole(formData); }
    fetchData();
    handleCloseModal();
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-6"><h1 className="text-3xl font-bold text-gray-800">Role Management</h1><button onClick={() => handleOpenModal()} className="flex items-center bg-primary text-white px-4 py-2 rounded-md"><FiPlus className="mr-2" /> Add New Role</button></div>
      <div className="bg-white shadow-md rounded-lg overflow-x-auto"><table className="min-w-full divide-y divide-gray-200"><thead className="bg-gray-50"><tr><th className="px-6 py-3 text-left">Name</th><th className="px-6 py-3 text-left">Permissions Count</th><th className="px-6 py-3 text-right">Actions</th></tr></thead><tbody className="bg-white divide-y divide-gray-200">{roles.map(role => (<tr key={role._id}><td className="px-6 py-4 font-semibold">{role.name}</td><td className="px-6 py-4">{role.permissions.length}</td><td className="px-6 py-4 text-right"><button onClick={() => handleOpenModal(role)} className="text-indigo-600"><FiEdit /></button></td></tr>))}</tbody></table></div>
      {isModalOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-start sm:items-center overflow-y-auto p-4"><div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-5xl max-h-[calc(100vh-2rem)] overflow-y-auto"><h2 className="text-xl font-bold mb-4">{editingRole ? 'Edit' : 'Add'} Role</h2><RoleForm role={editingRole} permissions={permissions} onSave={handleSave} onCancel={handleCloseModal} /></div></div>}
    </div>
  );
};

export default RolesPage;
